import { ChangeEventHandler, FC, memo, useEffect } from 'react';

import { FiChevronRight } from 'react-icons/fi';
import { useForm } from 'react-hook-form';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { usePasswordRecovery } from '~/modules/auth/hooks/usePasswordRecovery';

import { FormGroup } from '~/shared/components/Form/FormGroup';
import { CustomRadio } from '~/shared/components/Form/CustomRadio';
import { ControlledInput } from '~/shared/components/Form/ControlledInput';

import { maskCellPhone, unmask } from '~/shared/utils/masks';

import { TSendUserPasswordRecoveryCodeMethod } from '~/modules/auth/interfaces/ISendUserPasswordRecoveryCodeRequest';
import { IPasswordRecoveryFormData } from '~/modules/auth/interfaces/IPasswordRecoveryFormData';

import { StepWrapper } from '../StepWrapper';

const formSchemaValidation = (
  selectedMethod: TSendUserPasswordRecoveryCodeMethod
): Yup.AnyObjectSchema => {
  if (selectedMethod === 'email') {
    return Yup.object().shape({
      value: Yup.string()
        .email('E-mail inválido')
        .required('E-mail é obrigatório'),
    });
  }

  return Yup.object().shape({
    value: Yup.string()
      .phoneNumber('Telefone inválido')
      .required('Telefone é obrigatório'),
  });
};

const BaseStepTwo: FC = () => {
  const {
    currentStep,
    selectedMethod,
    sendRecoveryCodeError,
    sendRecoveryCode,
    selectedMethodOption,
    changeMethod,
  } = usePasswordRecovery();

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    handleSubmit,
    setError,
  } = useForm<Pick<IPasswordRecoveryFormData, 'value'>>({
    resolver: yupResolver(formSchemaValidation(selectedMethod)),
    defaultValues: { value: '' },
  });

  useEffect(() => {
    if (currentStep === 1) {
      setValue('value', '');
      setError('value', { message: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (typeof sendRecoveryCodeError !== 'undefined') {
      setError('value', { message: sendRecoveryCodeError });
    }
  }, [sendRecoveryCodeError, setError]);

  const handleChangeValue: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { value } = event.target;

    if (selectedMethod === 'email') {
      setValue('value', value, { shouldValidate: true });
    } else {
      setValue('value', maskCellPhone(unmask(value)), { shouldValidate: true });
    }
  };

  return (
    <StepWrapper
      step={2}
      onSubmit={handleSubmit(sendRecoveryCode)}
      isSubmitting={isSubmitting}
    >
      <FormGroup>
        <CustomRadio
          {...selectedMethodOption}
          description="Clique para alterar a forma"
          customEndIcon={FiChevronRight}
          onClick={changeMethod}
        />
      </FormGroup>

      <FormGroup>
        <ControlledInput
          control={control}
          name="value"
          size="lg"
          onChange={handleChangeValue}
          error={errors.value}
          maxLength={selectedMethod !== 'email' && 16}
          disableHookFormOnChange
          placeholder={
            selectedMethod === 'email'
              ? 'Digite o seu e-mail'
              : 'Digite o seu telefone'
          }
        />
      </FormGroup>
    </StepWrapper>
  );
};

export const StepTwo = memo(BaseStepTwo);
